import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  IconButton,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  EmojiEvents as TrophyIcon,
  LocalFireDepartment as StreakIcon,
  SportsScore as ScoreIcon,
  Psychology as QuestionIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthGen2 } from '../hooks/useAuthGen2';
import FriendsManager, { FriendProfile as FriendProfileType } from '../utils/FriendsManager';
import { fetchAuthSession } from 'aws-amplify/auth';

const FriendProfile: React.FC = () => {
  const navigate = useNavigate();
  const { friendId } = useParams<{ friendId: string }>();
  const { isAuthenticated, loading: authLoading } = useAuthGen2();
  const [profile, setProfile] = useState<FriendProfileType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading) {
      loadFriendProfile();
    }
  }, [friendId, authLoading, isAuthenticated]);

  const loadFriendProfile = async () => {
    if (!friendId) {
      setError('Invalid request');
      setLoading(false);
      return;
    }

    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (!token) {
        throw new Error('No authentication token found');
      }

      const profileData = await FriendsManager.getFriendProfile(friendId, token);
      setProfile(profileData);
    } catch (err) {
      console.error('Error loading friend profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to load friend profile');
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatDate = (timestamp?: number): string => {
    if (!timestamp) return 'Never';
    return new Date(timestamp).toLocaleDateString();
  };

  if (authLoading || loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4, textAlign: 'center', px: { xs: 0.25, sm: 0.2, md: 0.75 } }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading friend profile...
        </Typography>
      </Container>
    );
  }

  if (!isAuthenticated) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4, px: { xs: 0.25, sm: 0.2, md: 0.75 } }}>
        <Alert severity="info">
          Please log in to view friend profiles.
        </Alert>
      </Container>
    );
  }

  if (error || !profile) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4, px: { xs: 0.25, sm: 0.2, md: 0.75 } }}>
        <Alert severity="error">
          {error || 'Failed to load friend profile'}
        </Alert>
        <Box sx={{ mt: 2 }}>
          <IconButton onClick={() => navigate('/friends')} color="primary">
            <BackIcon />
          </IconButton>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4, px: { xs: 0.25, sm: 0.2, md: 0.75 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/friends')} sx={{ mr: 2 }}>
          <BackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          Friend Profile
        </Typography>
      </Box>

      {/* Profile Card */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar
            sx={{
              width: 80,
              height: 80,
              bgcolor: 'primary.main',
              fontSize: '2rem',
              mr: 3,
            }}
          >
            {getInitials(profile.displayName)}
          </Avatar>
          <Box>
            <Typography variant="h5" gutterBottom>
              {profile.displayName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Friends since {formatDate(profile.friendshipDate)}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Stats Overview */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <StreakIcon color="error" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" color="primary">
                {profile.statistics.currentStreak}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Current Streak
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrophyIcon color="warning" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" color="primary">
                {profile.statistics.totalWins}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Wins
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <ScoreIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" color="primary">
                {profile.statistics.winRate}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Win Rate
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <StarIcon color="info" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" color="primary">
                {profile.statistics.perfectGames}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Perfect Games
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Detailed Statistics */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Detailed Statistics
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Total Games Played
              </Typography>
              <Typography variant="h6">
                {profile.statistics.totalGamesPlayed}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Longest Streak
              </Typography>
              <Typography variant="h6">
                {profile.statistics.longestStreak}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Average Score
              </Typography>
              <Typography variant="h6">
                {profile.statistics.averageScore.toFixed(1)}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Average Guesses
              </Typography>
              <Typography variant="h6">
                {profile.statistics.averageGuesses.toFixed(1)}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Questions Asked
              </Typography>
              <Typography variant="h6">
                {profile.statistics.totalQuestions}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Trivia Answered
              </Typography>
              <Typography variant="h6">
                {profile.statistics.totalTrivia}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Last Played
              </Typography>
              <Typography variant="h6">
                {formatDate(profile.statistics.lastPlayedDate)}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Achievements */}
      {profile.achievements.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Achievements ({profile.achievements.length})
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {profile.achievements.map((achievement) => (
              <Chip
                key={achievement.achievementId}
                label={achievement.achievementId}
                icon={<TrophyIcon />}
                color="primary"
                variant="outlined"
              />
            ))}
          </Box>
        </Paper>
      )}
    </Container>
  );
};

export default FriendProfile;
